import { types } from '@concordia/super-sdk'
import { FormattedPositions, MetaBroker } from 'state/mock'
import { BORROW, DashTab } from 'state/slices/ui/dash'
import { buildIncentiveData, FormTab, openFormWithData } from 'state/slices/ui/form'
import { calcUtil } from 'toolbox/calc'
import { formatPercentage, prettyTokenBal } from 'toolbox/format'
import { COLLATERAL } from './CollateralRow'
import { useAppSelector } from 'state/hooks'
import ActionCircle from 'components/common/ActionCircle'
import { calcBorrowData, calcSupplyData } from 'state/slices/user/portfolio'
import ParamList from 'components/txForm/ParamList'
import { getPriceFromName, selectPricesFromBrokers } from 'state/slices/app/brokers'
import { selectIncentivesToggleOn } from 'state/slices/app/toggles'

export interface MobileBrokerCardProps {
  broker: MetaBroker
  tab: DashTab
  isLoadedUser: boolean
  positions: FormattedPositions
  balances: types.SWalletBalance[]
  showBalRow: boolean
  isLoadedApp: boolean
  formTabs: FormTab[]
  userCollat: number
  isLoadedBrokers: boolean
}

export function MobileBrokerCard({
  broker,
  tab,
  isLoadedUser,
  positions,
  balances,
  showBalRow,
  isLoadedApp,
  formTabs,
  userCollat,
  isLoadedBrokers
}: MobileBrokerCardProps) {
  const brokerBorrowed = broker?.scaledTotalBorrowedUnderlying
  const totalAvailable = broker?.scaledAvailableLiquidityUnderlying
  const brokerTotal = brokerBorrowed + totalAvailable
  const utilization = calcUtil(broker)
  const maxSupplyBroker = Number(broker?.maxDepositScaled)
  const t = broker.tokenMeta
  const prices = useAppSelector(selectPricesFromBrokers)
  const aptPrice = getPriceFromName('aptos', prices)
  const incentivesToggleOn = useAppSelector(selectIncentivesToggleOn)

  const borrowData = calcBorrowData(positions, broker)
  const supplyData = calcSupplyData(positions, broker)
  const incentiveData = buildIncentiveData(broker, aptPrice)

  const { supplyRewardRate, borrowRewardRate } = incentiveData

  const maxParam =
    tab === BORROW
      ? null
      : {
          label: 'Pool max limit',
          value: prettyTokenBal(maxSupplyBroker),
          tooltip: 'Maximum liquidity that can be supplied to the pool'
        }

  //broker params and projections
  const brokerStats = [
    {
      label: 'Lend APR',
      value: formatPercentage(supplyData.supplyAPR + supplyRewardRate),
      tooltip: `Total lend APR`
    },
    {
      label: 'Borrow APR',
      value: formatPercentage(borrowData.borrowAPR - borrowRewardRate),
      tooltip: `Total borrow APR`
    },
    {
      label: 'Total Supplied In Broker',
      value: prettyTokenBal(brokerTotal),
      tooltip: 'Liquidity supplied in tokens'
    },
    {
      label: 'Utilization',
      value: formatPercentage(utilization),
      tooltip: 'Ratio of debt / collateral.  High utilization increases interest'
    }
  ]

  const isSupply = tab === COLLATERAL
  const hasSupplied = userCollat > 0
  const hasPower = 0 < 1
  const canBorrow = hasSupplied && hasPower

  const openAdd = () => openFormWithData(broker, incentiveData, formTabs[0])
  const open = () => openFormWithData(broker, incentiveData, formTabs[1])
  const disAddDeposit = !isLoadedUser
  const disAddBorrow = !isLoadedUser || !canBorrow

  const disRemoveDeposit = !isLoadedUser || !supplyData.hasPosition
  const disRemoveBorrow = !isLoadedUser || !borrowData.hasPosition

  const disabledAddSwitch = (tab: DashTab) => {
    switch (tab) {
      case COLLATERAL:
        return disAddDeposit
      case BORROW:
        return disAddBorrow
    }
  }

  const disabledMinusSwitch = (tab: DashTab) => {
    switch (tab) {
      case COLLATERAL:
        return disRemoveDeposit
      case BORROW:
        return disRemoveBorrow
    }
  }
  const buttons = (
    <div className="broker-buttons">
      <ActionCircle cb={openAdd} disabled={disabledAddSwitch(tab)} isAdd isSupply={isSupply} />
      <ActionCircle cb={open} disabled={disabledMinusSwitch(tab)} isSupply={isSupply} />
    </div>
  )
  return (
    <div className="mobile-broker-card">
      <div className="top-card">
        <div className="basic-meta small">
          <img src={t.logo} alt={t.name} />
          <div className="names">
            <p className="name">{t.ticker}</p>
            <p className="full-name">{t.displayName}</p>
          </div>
        </div>
        {buttons}
      </div>
      <hr className="hr" />
      <ParamList params={brokerStats} />
    </div>
  )
}
